import * as React from 'react';
import { ICloneOrderCopyDetails, ICloneOrderSourceDetails, ISelectOption } from '../OrdersApi';
import { handleErrorAsync } from '../../../shared/ErrorCatching';
import { useCloneOrderMutation } from './queries/cloneOrderMutation';
import { useCloneOrderSourceDetails } from './queries/cloneOrderSourceDetails';

export const MIN_CLONE_COUNT = 1;
export const MAX_CLONE_COUNT = 99;
export const APARTMENT_OBJECT_TYPE = 1;

const MIN_YEAR = 1500;
const MAX_YEAR = 2200;

interface IUseCloneOrderModalProps {
    orderId: number;
    show: boolean;
    onHide: () => void;
    onResult: (success: boolean) => void;
}

function makeDefault(source?: ICloneOrderSourceDetails): ICloneOrderCopyDetails {
    if (!source) {
        return {
            notes: '',
            objectAreaSquareMeters: '',
            objectNumberOfRooms: '',
            objectYearOfConstruction: '',
            objectType: 0,
            apartmentHasBalcony: null,
            apartmentFloor: '',
            apartmentAccessCode: '',
            orderCollectionName: ''
        };
    }
    return {
        notes: source.notes || '',
        objectAreaSquareMeters: source.objectAreaSquareMeters || '',
        objectNumberOfRooms: source.objectNumberOfRooms || '',
        objectYearOfConstruction: source.objectYearOfConstruction || '',
        objectType: source.objectType || 0,
        // Preserve an unknown (null) balcony state rather than collapsing it to false, so an
        // untouched copy lets the backend fall back to the source order's own value.
        apartmentHasBalcony: source.apartmentHasBalcony,
        apartmentFloor: source.apartmentFloor || '',
        apartmentAccessCode: source.apartmentAccessCode || '',
        orderCollectionName: source.orderCollectionName || ''
    };
}

function resize(
    prev: ICloneOrderCopyDetails[],
    size: number,
    source?: ICloneOrderSourceDetails
): ICloneOrderCopyDetails[] {
    if (prev.length === size) {
        return prev;
    }
    if (prev.length > size) {
        return prev.slice(0, size);
    }
    const grown = prev.slice();
    while (grown.length < size) {
        grown.push(makeDefault(source));
    }
    return grown;
}

function isNonNegativeNumberOrEmpty(value: string): boolean {
    if (!value) {
        return true;
    }
    const n = Number(value);
    return Number.isFinite(n) && n >= 0;
}

function isPlausibleYearOrEmpty(value: string): boolean {
    if (!value) {
        return true;
    }
    return /^\d+$/.test(value) && Number(value) >= MIN_YEAR && Number(value) <= MAX_YEAR;
}

export function isCopyValid(copy: ICloneOrderCopyDetails): boolean {
    return (
        copy.orderCollectionName.trim().length > 0 &&
        isNonNegativeNumberOrEmpty(copy.objectAreaSquareMeters) &&
        isNonNegativeNumberOrEmpty(copy.objectNumberOfRooms) &&
        isPlausibleYearOrEmpty(copy.objectYearOfConstruction)
    );
}

export function useCloneOrderModal({ orderId, show, onHide, onResult }: IUseCloneOrderModalProps) {
    const [count, setCount] = React.useState<number>(MIN_CLONE_COUNT);
    const [copies, setCopies] = React.useState<ICloneOrderCopyDetails[]>([]);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);

    const {
        data: source,
        isLoading: isSourceLoading,
        isError: isSourceError,
        refetch: refetchSource
    } = useCloneOrderSourceDetails(orderId, show);
    const cloneOrder = useCloneOrderMutation(orderId);

    const seededRef = React.useRef(false);

    React.useEffect(() => {
        if (show && source && !seededRef.current) {
            seededRef.current = true;
            setCopies(Array.from({ length: count }, () => makeDefault(source)));
        }
    }, [show, source, count]);

    React.useEffect(() => {
        if (!show) {
            seededRef.current = false;
            setCopies([]);
            setSelectedIndex(0);
            setCount(MIN_CLONE_COUNT);
        }
    }, [show]);

    const isCountValid =
        Number.isInteger(count) && count >= MIN_CLONE_COUNT && count <= MAX_CLONE_COUNT;

    const changeCount = (next: number) => {
        setCount(next);
        if (Number.isInteger(next) && next >= MIN_CLONE_COUNT && next <= MAX_CLONE_COUNT) {
            setCopies((prev) => resize(prev, next, source));
            setSelectedIndex((idx) => Math.min(idx, next - 1));
        }
    };

    const updateSelected = (patch: Partial<ICloneOrderCopyDetails>) => {
        setCopies((prev) => prev.map((c, i) => (i === selectedIndex ? { ...c, ...patch } : c)));
    };

    const applyToAll = () => {
        const current = copies[selectedIndex];
        if (!current) {
            return;
        }
        // Share only the object details. The collection name identifies each copy (every copy
        // becomes its own order collection), so each copy keeps the name it already has.
        setCopies((prev) =>
            prev.map((c) => ({ ...current, orderCollectionName: c.orderCollectionName }))
        );
    };

    const isModified = (copy: ICloneOrderCopyDetails): boolean =>
        JSON.stringify(copy) !== JSON.stringify(makeDefault(source));

    let objectTypeOptions: ISelectOption[] = [];
    if (source) {
        objectTypeOptions = source.isCommercial
            ? source.availableCommercialObjectTypes
            : source.availablePrivateObjectTypes;
    }

    const areCopiesValid = copies.length > 0 && copies.every(isCopyValid);
    const isCreateDisabled =
        !isCountValid || !areCopiesValid || isLoading || isSourceLoading || isSourceError || !source;

    const handleCreate = async () => {
        if (isCreateDisabled) {
            return;
        }
        setIsLoading(true);
        try {
            await cloneOrder(count, copies);
            onHide();
            onResult(true);
        } catch (e) {
            // The API answers a rejected clone with its own translated reason (invalid-year,
            // copies-count-mismatch, source-not-found, …). Let the shared handler surface that
            // reason and keep the modal open so the typed copies survive and can be corrected.
            // It fails when there is no reason to show — a network error, or a body that is not
            // JSON (empty 500, HTML 504 from a proxy) — and that falls back to the generic
            // failure snackbar rather than leaving the modal open with no message.
            try {
                await handleErrorAsync(e);
            } catch (withoutReason) {
                onHide();
                onResult(false);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return {
        count,
        changeCount,
        copies,
        selectedIndex,
        setSelectedIndex,
        selectedCopy: copies[selectedIndex],
        updateSelected,
        applyToAll,
        isModified,
        objectTypeOptions,
        isCommercial: source ? source.isCommercial : false,
        isLoading,
        isSourceLoading,
        isSourceError,
        refetchSource,
        isCountValid,
        isCreateDisabled,
        handleCreate
    };
}
